import { Stack, Typography } from '@mui/material'
import { FC, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined'
import { Summary } from './Summary/Summary'
import { useAlert } from '~/@ui/alert/useAlert'
import { IInvestmentOrder } from '~/@api/generated-api-types/global.types'
import { getApi } from '~/@utils/api/getApi'
import { getErrorMessage } from '~/@utils/api/getErrorMessage'
import { InfoCard } from '~/@ui/info-card/InfoCard'
import { AddFundsModal } from './AddFundsModal'
import { calculateInvestmentProjections } from '~/@ui/chart/_utils/investmentCalculator'
import { OpportunityAvatar } from '../opportunities/OpportunityAvatar'
import { LoadingWheelPurple } from '~/@ui/info-card/LoadingWheels/LoadingWheelPurple'
import { LoadingWheelOrange } from '~/@ui/info-card/LoadingWheels/LoadingWheelOrange'
import { COLOR_PALLETE } from '~/COLOR_PALLETE'

export const InvestmentPage: FC = () => {
  const navigate = useNavigate()
  const { showAlert } = useAlert()

  const { investmentId = '' } = useParams()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [investment, setInvestment] = useState<IInvestmentOrder | null>(null)
  const [variableSymbol, setVariableSymbol] = useState<null | string>(null)

  const isSavingsRetirement =
    investment?.productSnapshot.slug === 'zabezpecenie-financii-na-dochodok'

  const getInvestment = async () => {
    try {
      const { data: response } =
        await getApi().investmentOrdersAppApiControllerGetInvestmentOrder(
          investmentId
        )

      if ('success' in response) {
        setInvestment(response.investmentOrder)
        return
      }

      throw new Error('Nasatala neznáma chyba pri načítavaní investície.')
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  const getVariableSymbol = async () => {
    try {
      const { data: response } =
        await getApi().investorProfileAppApiControllerGetInvestorVariableSymbol()

      if ('success' in response) {
        setVariableSymbol(response.variableSymbol)
        return
      }

      throw new Error('Nastala chyba pri načítavaní investičných príležitostí.')
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  const oneTimeInvestmentAmount = investment?.hasOneTimeInvestment
    ? investment.oneTimeInvestmentAmount || 0
    : 0
  const monthlyInvestmentAmount = investment?.monthlyInvestmentAmount || 0
  const initialDeposit = oneTimeInvestmentAmount + monthlyInvestmentAmount

  const investmentYears = isSavingsRetirement ? 41 : 33

  const series = useMemo(
    () =>
      calculateInvestmentProjections({
        monthlyContribution: monthlyInvestmentAmount,
        investmentYears,
        initialDeposit,
        rates: {
          earlyWithdraw: 0.0359,
          bonaship: 0.0718,
          cash: 0,
          bankSavings: 0.015,
          inflation: 0.04,
        },
      }),
    [monthlyInvestmentAmount, investmentYears, initialDeposit]
  )

  useEffect(() => {
    getInvestment()
    getVariableSymbol()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack className="w-screen">
      <Stack className="flex-row h-[70px] items-center gap-3 px-6 py-6">
        <ArrowBackIosNewOutlinedIcon
          className="mb-1"
          onClick={() => navigate(-1)}
        />
        <Stack className="">
          <Typography className="text-base font-semibold text-zBlack200 ">
            Detail investície
          </Typography>
          <Typography className="text-xs font-medium text-zGrey400">
            {`ID: ${investment?.publicId || ''}`}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        className="w-full max-w-[768px] self-center pt-6 pb-28 rounded-t-4d bg-zBlack200"
        sx={{ minHeight: 'calc(100vh - 70px)' }}>
        <Stack className="flex-row items-center gap-2 pb-6 mx-6 ">
          <OpportunityAvatar
            slug={investment?.productSnapshot.slug}
            mode="dark"
          />
          <Stack className="">
            <Typography className="text-base font-semibold text-zWhite200">
              {investment?.productSnapshot.name}
            </Typography>
          </Stack>
        </Stack>

        {investment?.status === 'created' && (
          <InfoCard
            className="mx-6 mb-6"
            bgColor={COLOR_PALLETE.zOrange500}
            btnColor={COLOR_PALLETE.zOrange400}
            icon={<LoadingWheelOrange className="flex-shrink-0 w-5 h-5" />}
            headerText="Stav objednávky"
            bodyText="Čakáme na dobitie vašich prostriedkov."
            linkText="Pridať prostriedky"
            onClick={() => setIsModalOpen(true)}
          />
        )}
        {investment?.status === 'pairing' && (
          <InfoCard
            className="mx-6 mb-6"
            icon={<LoadingWheelPurple className="flex-shrink-0 w-5 h-5" />}
            bgColor="zGrey200"
            btnColor="#DED4FF"
            headerText="Stav objednávky"
            bodyText="Vašu investíciu začneme párovať so seniorom. Urobíme všetko pre to, aby začala čo najskôr zarábať. Ak sa nám to nepodarí do 60 dní, investíciu vám automaticky vrátime."
          />
        )}

        <Summary investment={investment} series={series} />
      </Stack>

      <AddFundsModal
        isOpen={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        variableSymbol={variableSymbol}
      />
    </Stack>
  )
}
